import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { tooltip } from '../helpers/tooltip';
import auth from '../../apis/auth';

const ValueModelCreateForm = forwardRef(({ products, editCustomerSegmentId, editCustomerSegment, formActions, onSubmit }, ref) => {
  const [options, setOptions] = useState([]);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [search, setSearch] = useState('');
  const [isGeneral, setIsGeneral] = useState(false);

  // Set shouldSubmit to true 1 second after any field is changed
  const startSubmitClock = () => {
    setShouldSubmit(false);
    setTimeout(() => {
      setShouldSubmit(true);
    }, 1000);
  };

  // Submit the form when shouldSubmit is true
  useEffect(() => {
    if (shouldSubmit && search.length > 0) {
      fetchOptions(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSubmit]);

  const fetchOptions = async (search_term) => {
    await auth.get(`/api/value/search_industries?search_term=${search_term}`)
      .then(response => {
        setOptions(response.data.relevant_industries);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      });
  };

  const initialValues = {
    name: '',
    industry: {},
    product: Object.values(products)[0].name
  };

  const validate = (values) => {
    const errors = {};
    if (!values.name) {
      errors.name = 'Name is required';
    }
    if (!values.industry || Object.keys(values.industry).length === 0) {
      errors.industry = 'Industry is required';
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: initialValues,
    validate: validate,
    onSubmit: onSubmit,
  });

  useImperativeHandle(ref, () => ({
    resetForm() {
      formik.handleReset();
    },
  }));

  useEffect(() => {
    if (editCustomerSegmentId) {
      formik.setFieldValue('name', editCustomerSegment['name']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCustomerSegmentId]);

  // Handle switch toggle
  const handleSwitchChange = (event) => {
    const general = event.target.checked;
    setIsGeneral(general);
    if (general) {
      formik.setFieldValue('industry', { industry: 'General', code: 'GENERAL' });
    } else {
      formik.setFieldValue('industry', {});
    }
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} maxWidth={400}>
      <Stack direction="column" spacing={2}>
        <TextField
          name="name"
          label="Value Model Name"
          value={formik.values.name}
          onChange={formik.handleChange}
          size="small"
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />

        {/* Industry switch and label */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Industry
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Specific</Typography>
            <Switch
              size="small"
              checked={isGeneral}
              onChange={handleSwitchChange}
              sx={{
                '& .MuiSwitch-thumb': {
                  backgroundColor: 'green',
                },
                '& .Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: 'green',
                }
              }}
            />
            <Typography variant="body2">General</Typography>
          </Box>
        </Box>

        {/* Autocomplete shown only if Specific */}
        {!isGeneral && (
          <Autocomplete
            name="industry"
            disableClearable
            options={options}
            getOptionLabel={(option) => option.industry || ''}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) =>
              Object.keys(value).length === 0 || option.code === value.code
            }
            size="small"
            sx={{ mr: '8px' }}
            value={formik.values.industry}
            onChange={(event, value) => {
              formik.setFieldValue('industry', value || {});
            }}
            onInputChange={(event, value) => {
              setSearch(value);
              startSubmitClock();
            }}
            onBlur={formik.handleBlur}
            renderOption={(props, option) => (
              <li {...props} key={option.code}>
                {option.industry}
                {tooltip(option.description)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Industry"
                error={getIn(formik.touched, 'industry') && Boolean(getIn(formik.errors, 'industry'))}
                helperText={
                  getIn(formik.touched, 'industry') && getIn(formik.errors, 'industry')
                    ? getIn(formik.errors, 'industry')
                    : 'Please search for industry and choose one from the list'
                }
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      {formik.values.industry && Object.keys(formik.values.industry).length > 0 && tooltip(formik.values.industry.description)}
                    </>
                  ),
                }}
                fullWidth
              />
            )}
          />
        )}

        {formActions()}
      </Stack>
    </Box>
  );
});

export default ValueModelCreateForm;
